import { FC, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useMutation } from '@tanstack/react-query';
import { urlValidation } from '@utils/patterns';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { useMyQuery } from '@hooks/useMyQuery';
import { useS3ImageUploader } from '@hooks/useS3ImageUploader';
import { useEntityFormMaxHeight } from '@hooks/useEntityFormMaxHeight';
import { Input } from '@inputs/Input/Input';
import { SelectComponent } from '@inputs/SelectComponent/SelectComponent';
import { Button } from '@components/Core/Button';
import { ImageUploader } from '@components/Core/ImageUploader/ImageUploader';
import { EntityStatusRow } from '@components/Core/EntityStatusRow';
import BrandFormPopups from '@components/BrandFormPopups';
import { DEFAULT_NAME_MIN_LENGTH } from '@hooks/useValidation';
import { BrandsTransFields } from '@pages/Brands/types';
import { brandsTranslations } from '@pages/Brands/translations';
import CloseIcon from '@icons/close_icon.svg?component';
import { IBusinessBrandData } from '@stores/brands/types';
import { BrandFormTransFields, IBrandFormWithFiles } from './types';
import { brandFormTranslations } from './translations';

const BrandForm: FC<{ brand?: IBusinessBrandData }> = ({ brand }) => {
  const {
    alertStore: { showAlert },
    brandsStore: {
      getBrandsMarkers,
      getCountryCodes,
      countryCodes,
      markers,
      addBrand,
      editBrand,
      changeStatus,
    },
    profileStore: { user },
    mainStore: { selectedCountryCode },
    popupStore: { openPopup, closePopup },
  } = useStore();

  const { isFetching } = useMyQuery({
    queryKey: ['getCountryCodes'],
    queryFn: () => getCountryCodes(),
    enabled: !brand,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: addBrand,
    mutationKey: ['addBrand'],
    onSuccess: () => {
      showAlert(t('addBrandSuccess'), 'success');
      navigate('/brands');
    },
  });

  const { mutate: editBrandMutation, isPending: editBrandPending } = useMutation({
    mutationFn: editBrand,
    mutationKey: ['editBrand'],
    onSuccess: () => {
      showAlert(t('editBrandSuccess'), 'success');
      navigate('/brands');
    },
  });

  const { mutate: changeStatusMutation, isPending: changeStatusPending } = useMutation({
    mutationFn: changeStatus,
    mutationKey: ['changeStatus'],
    onSuccess: () => {
      showAlert(t('changeStatusSuccess'), 'success');
    },
  });

  const { t } = useMyTranslations<BrandFormTransFields | Extract<BrandsTransFields, 'addBrand'>>([
    brandFormTranslations.name,
    brandsTranslations.name,
  ]);

  const navigate = useNavigate();

  useEffect(() => {
    if (brand?.adminMessage) {
      openPopup('status');
    }
  }, [brand?.adminMessage]);

  const useFormMethods = useForm<IBrandFormWithFiles>({
    defaultValues: {
      ...brand,
      countryCodeData: brand
        ? user?.usersCountryCodes.find((item) => item.countryCode === brand.countryCode)
        : selectedCountryCode || undefined, // use countryCodeData from brands country code or from selectedCountryCode
    },
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { isDirty },
  } = useFormMethods;

  const { isFetching: isFetchingMarkers } = useMyQuery({
    queryKey: ['getBrandsMarkers', watch('countryCodeData')?.countryCode],
    queryFn: () => {
      if (watch('countryCodeData')?.countryCode)
        return getBrandsMarkers(watch('countryCodeData').countryCode);
      return null;
    },
    enabled: true,
    staleTime: 0,
  });

  const { uploadImage, isLoading: isImageUploading } = useS3ImageUploader<IBrandFormWithFiles>(
    (val) => {
      if (brand) editBrandMutation({ ...brand, ...val });
      else mutate(val);
    }
  );

  const sendRequest = useCallback(
    async (formData: IBrandFormWithFiles) => {
      const { logoUrlFile } = formData;
      if (logoUrlFile)
        await uploadImage(formData, logoUrlFile, 'logoUrl', `bussiness-profile/${user?.id}/brands`);
      else {
        if (brand) editBrandMutation({ ...brand, ...formData });
        else mutate(formData);
      }
      closePopup();
    },
    [brand, user]
  );

  const onSubmit = useCallback(
    (formData: IBrandFormWithFiles) => {
      if (brand) sendRequest(formData);
      else {
        openPopup('confirmation');
      }
    },
    [brand]
  );

  const renderSubmitBtn = useCallback(
    (place: string) => (
      <Button
        text={brand ? t('save') : t('addBrand', { ns: brandsTranslations.name })}
        onClick={handleSubmit(onSubmit)}
        disabled={
          isImageUploading ||
          isPending ||
          editBrandPending ||
          changeStatusPending ||
          isFetching ||
          isFetchingMarkers ||
          !isDirty
        }
        styleProp={`max-w-[170px] sm-brand-form-submit ${
          place === 'inside' ? 'md-brand-form-submit-hide' : 'md-brand-form-submit-show'
        }`}
      />
    ),
    [
      isImageUploading,
      isPending,
      editBrandPending,
      changeStatusPending,
      brand,
      isFetching,
      isFetchingMarkers,
      isDirty,
    ]
  );

  const maxHeight = useEntityFormMaxHeight();

  return (
    // use ts-ignore due to Type instantiation is excessively deep and possibly infinite.ts(2589) error
    // @ts-ignore
    <>
      <FormProvider {...useFormMethods}>
        <form
          id="form"
          className="flex gap-[32px] px-[48px] pb-[48px] sm-page-padding md-brand-form-right overflow-scroll"
          style={{ maxHeight }}
        >
          <section className="section">
            <div className="bg-white flex flex-col shadow-sm p-[24px] rounded-[16px] gap-y-[24px] w-[640px] md-brand-form-inputs">
              <h6 className="subtitle !mb-0 !uppercase">{t('basic')}</h6>
              <Input
                name={`name`}
                type="text"
                placeholder={t('name')}
                label={t('name')}
                required
                showRequiredIcon
                minLength={DEFAULT_NAME_MIN_LENGTH}
              />
              <Input
                name="website"
                type="text"
                placeholder={t('website')}
                validation={urlValidation()}
                label={t('website')}
              />
              <SelectComponent
                name="countryCodeData"
                placeholder={t('countryCodeData')}
                label={t('countryCodeData')}
                options={countryCodes || []}
                optionLabel="name"
                optionValue="countryCode"
                required
                showRequiredIcon
                isDisabled={!!brand || isFetching}
                menuPlacement="bottom"
              />
              {brand && (
                <EntityStatusRow
                  status={brand.status}
                  onChange={(status) => changeStatusMutation({ id: brand.id, status })}
                  disabled={
                    isImageUploading || isPending || editBrandPending || changeStatusPending
                  }
                />
              )}
            </div>
            {renderSubmitBtn('inside')}
          </section>

          <section className="section md-brand-form-left sm-brand-form-left">
            <ImageUploader name="logoUrl" title={t('logoUrl')} required />
            <ImageUploader name="pointerUrl" title={t('pointerUrl')} required>
              {watch('pointerUrl') ? (
                <div className="flex-centred bg-[#F3F5FC] p-[64px] rounded-[8px] relative">
                  <div className="flex-centred w-[30%] min-w-[112px] h-[112px] p-[20px] bg-container-bg rounded-[12px]">
                    <img
                      src={getValues('pointerUrl')}
                      alt="point image"
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <button
                    className="p-0 cursor-pointer absolute bg-none bottom-[16px] right-[16px] rounded-[50%] transition-all hover:bg-[rgba(white, 0.3)]"
                    type="button"
                    onClick={() => setValue('pointerUrl', '', { shouldDirty: true })}
                  >
                    <CloseIcon width={16} height={16} />
                  </button>
                </div>
              ) : (
                <Button
                  text={t('addMarker')}
                  onClick={() => openPopup('markers')}
                  disabled={
                    !markers ||
                    isImageUploading ||
                    isPending ||
                    editBrandPending ||
                    changeStatusPending ||
                    isFetchingMarkers
                  }
                  styleType="secondary"
                />
              )}
            </ImageUploader>
          </section>
          {renderSubmitBtn('outside')}
        </form>
        <BrandFormPopups sendRequest={sendRequest} adminMessage={brand?.adminMessage} />
      </FormProvider>
    </>
  );
};

export default observer(BrandForm);
